<template>
  <div :class="['editor-wrapper', { 'focus-mode': isFocusMode }]">
    <button
      class="focus-mode-btn"
      @click="toggleFocusMode">
      {{ isFocusMode ? '🡼' : '⛶' }}
    </button>
    <div
      ref="editorContainer"
      class="editor-container" />
  </div>
</template>

<script setup>
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import 'monaco-editor/esm/vs/language/json/monaco.contribution';
import 'monaco-editor/esm/vs/language/css/monaco.contribution';
import { v4 as uuidv4 } from 'uuid';
import {
  ref, onMounted, onBeforeUnmount, watch, nextTick,
} from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Object],
    default: '',
  },
  language: {
    type: String,
    default: 'json',
  },
  theme: {
    type: String,
    default: 'vs-dark',
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:hasErrors',
  'editor-ready',
]);

const editorContainer = ref(null);
const isFocusMode = ref(false);
let editorInstance = null;
let modelUri = null;

const toggleFocusMode = () => {
  isFocusMode.value = !isFocusMode.value;
  document.body.style.overflow = isFocusMode.value ? 'hidden' : '';
  setTimeout(() => {
    editorInstance.layout();
  }, 0);
};

const toEditorValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

onMounted(async () => {
  modelUri = monaco.Uri.parse(`inmemory://model-${uuidv4()}.json`);

  const content = toEditorValue(props.modelValue);

  const model = monaco.editor.createModel(content, props.language, modelUri);

  editorInstance = monaco.editor.create(editorContainer.value, {
    model,
    theme: props.theme,
    automaticLayout: true,
    renderLineHighlight: 'none',
    readOnly: props.readOnly,
  });

  model.onDidChangeContent(() => {
    emit('update:modelValue', model.getValue());
  });

  const modelUriString = modelUri.toString();
  const disposeMarkers = monaco.editor.onDidChangeMarkers((uris) => {
    if (!uris.some((uri) => uri.toString() === modelUriString)) return;
    const markers = monaco.editor.getModelMarkers({ resource: modelUri });
    const hasErr = markers.some((marker) => marker.severity === monaco.MarkerSeverity.Error);
    emit('update:hasErrors', hasErr);
  });
  editorContainer.value.disposeMarkers = disposeMarkers;

  document.addEventListener('keydown', (event) => {
    if (isFocusMode.value && event.key === 'Escape') {
      toggleFocusMode();
    }
  });

  await nextTick();
  emit('editor-ready');
});

watch(() => props.modelValue, (newVal) => {
  if (editorInstance) {
    const model = editorInstance.getModel();
    const currentValue = model.getValue();
    const newValue = toEditorValue(newVal);

    if (currentValue !== newValue) {
      model.setValue(newValue);
    }
  }
});

watch(() => props.readOnly, (newVal) => {
  if (editorInstance) {
    editorInstance.updateOptions({ readOnly: newVal });
  }
});

onBeforeUnmount(() => {
  editorInstance?.dispose();
  document.body.style.overflow = '';
  editorContainer.value?.disposeMarkers?.dispose();

  if (modelUri) {
    const modelUriString = modelUri.toString();
    const currentOptions = monaco.languages.json.jsonDefaults.diagnosticsOptions;

    if (currentOptions.schemas) {
      const schemas = currentOptions.schemas.filter((schemaConfig) => !schemaConfig.fileMatch
        || !schemaConfig.fileMatch.some((match) => match.includes(modelUriString)));

      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        ...currentOptions,
        schemas,
      });
    }

    const model = monaco.editor.getModel(modelUri);
    if (model) {
      model.dispose();
    }
  }
});

const setSchema = (schema) => {
  if (props.language !== 'json') return;
  if (!editorInstance || !modelUri) return;

  try {
    const currentOptions = monaco.languages.json.jsonDefaults.diagnosticsOptions;
    const existingSchemas = currentOptions.schemas || [];

    const modelUriString = modelUri.toString();
    const filteredSchemas = existingSchemas.filter((schemaConfig) => !schemaConfig.fileMatch
      || !schemaConfig.fileMatch.some((match) => match.includes(modelUriString)));

    const schemaWithAdditionalProperties = {
      ...schema,
      additionalProperties: true,
    };

    const newSchemaConfig = {
      uri: schema.$id || schema.id || `http://${modelUriString}-schema`,
      fileMatch: [modelUriString],
      schema: schemaWithAdditionalProperties,
    };

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      ...currentOptions,
      schemas: [...filteredSchemas, newSchemaConfig],
    });

    const model = editorInstance.getModel();
    if (model) {
      monaco.editor.setModelMarkers(model, 'json', []);
      const value = model.getValue();
      model.setValue(value);
    }
  } catch (error) {
    console.error('Error setting schema:', error);
  }
};

const setJsonDiagnosticsOptions = (options) => {
  if (!editorInstance || !modelUri) return;

  const modelUriString = modelUri.toString();
  const currentOptions = monaco.languages.json.jsonDefaults.diagnosticsOptions;

  if (options.schemas) {
    options.schemas = options.schemas.map((schemaConfig) => ({
      ...schemaConfig,
      fileMatch: [modelUriString],
      schema: {
        ...schemaConfig.schema,
        additionalProperties: true,
      },
    }));
  }

  const existingSchemas = currentOptions.schemas?.filter((schemaConfig) => !schemaConfig.fileMatch
    || !schemaConfig.fileMatch.some((match) => match.includes(modelUriString))) || [];

  monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
    ...currentOptions,
    ...options,
    schemas: [...existingSchemas, ...(options.schemas || [])],
  });
};

const getEditor = () => editorInstance;

defineExpose({ getEditor, setJsonDiagnosticsOptions, setSchema });
</script>

<style scoped>
.editor-wrapper {
  position: relative;
  width: 100%;
  height: 500px;
  transition: all 0.2s ease;
}

.editor-container {
  width: 100%;
  height: 100%;
}

.focus-mode-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background-color: #333;
  color: #fff;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.focus-mode-btn:hover {
  opacity: 1;
}

.focus-mode {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: white;
  width: 40vw;
}

  @media (max-width: 768px) {
    .focus-mode {
      width: 100vw;
    }
  }

.focus-mode .editor-container {
  height: 100vh;
}
</style>
